import React, { useState, useEffect, useRef } from 'react';
import { Typography, Button, Box, Grid, TextField, IconButton, Tooltip, Snackbar, Alert, ClickAwayListener, Fade, Grow, Collapse, CircularProgress, useTheme, useMediaQuery, Paper, alpha, InputAdornment } from '@mui/material';
import { styled } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/DeleteOutline';
import DescriptionIcon from '@mui/icons-material/Description';
import SaveIcon from '@mui/icons-material/Save';
import InfoIcon from '@mui/icons-material/Info';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import ExtensionOutlinedIcon from '@mui/icons-material/ExtensionOutlined';
import { useTranslation } from 'react-i18next';
import { useDashboard } from './Dashboard/DashboardContext';
import { useParams } from 'react-router-dom';
import { apiRequest } from '../Utils/apiClient';
import { generatePastelColor, generateSaturatedColor } from '../Utils/colorUtils';
import { hasUserTagVariableReservedChars } from '../Utils/tagUtils';

// Theme colors matching TimeEventTag
const primaryColor = '#5757b0';
const lightPurple = '#f0f0ff';
const ALL_TAG_GROUP = 'ALL';
const TAG_GROUP_FIELD = 'tagGroup';
const LEGACY_TAG_GROUP_FIELD = 'group';

const normalizeExtensions = (extensions) => {
  const values = Array.isArray(extensions)
    ? extensions
    : String(extensions || '').split(',');
  return [...new Set(values
    .map((extension) => String(extension || '').trim().toLowerCase())
    .filter(Boolean)
    .map((extension) => (extension.startsWith('.') ? extension : `.${extension}`)))];
};

const normalizeTagDefinition = (tag) => {
  const tagGroup = String(
    tag?.[TAG_GROUP_FIELD] ?? tag?.[LEGACY_TAG_GROUP_FIELD] ?? ALL_TAG_GROUP,
  ).trim() || ALL_TAG_GROUP;
  const normalizedTag = {
    ...tag,
    variable: String(tag?.variable || ''),
    description: String(tag?.description || ''),
    [TAG_GROUP_FIELD]: tagGroup,
    extensions: tagGroup.toLowerCase() === ALL_TAG_GROUP.toLowerCase()
      ? []
      : normalizeExtensions(tag?.extensions),
  };
  delete normalizedTag[LEGACY_TAG_GROUP_FIELD];
  return normalizedTag;
};

const toApiTagDefinition = (tag) => {
  const normalized = normalizeTagDefinition(tag);
  const apiTag = { ...normalized };
  delete apiTag.__tagId;
  return apiTag;
};

const parseTagDefinitions = (value) => {
  let parsed = value;
  if (typeof parsed === 'string') {
    try {
      parsed = JSON.parse(parsed);
    } catch (error) {
      return [];
    }
  }
  return Array.isArray(parsed) ? parsed.map(normalizeTagDefinition) : [];
};

const comparableTagDefinition = (tag) => {
  const comparableTag = { ...normalizeTagDefinition(tag) };
  delete comparableTag.__tagId;
  return comparableTag;
};

const comparableTagGroupMetadata = (tag) => {
  const normalizedTag = normalizeTagDefinition(tag);
  return {
    [TAG_GROUP_FIELD]: normalizedTag[TAG_GROUP_FIELD],
    extensions: normalizedTag.extensions,
  };
};

const areTagGroupMetadataEqual = (left, right) => (
  JSON.stringify(comparableTagGroupMetadata(left))
  === JSON.stringify(comparableTagGroupMetadata(right))
);

const areTagDefinitionsEqual = (left = [], right = []) => (
  JSON.stringify(left.map(comparableTagDefinition)) === JSON.stringify(right.map(comparableTagDefinition))
);

const StyledTagPaper = styled(Paper)(({ theme, backgroundColor }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  borderRadius: '16px',
  background: `linear-gradient(135deg, white 0%, ${backgroundColor || lightPurple}15 100%)`,
  border: 'none',
  borderLeft: `6px solid ${backgroundColor || primaryColor}`,
  boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  overflow: 'hidden',
  '&:hover': {
    boxShadow: '0 8px 24px rgba(87, 87, 176, 0.12)',
    transform: 'translateY(-2px)',
    background: `linear-gradient(135deg, white 0%, ${backgroundColor || lightPurple}25 100%)`,
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `linear-gradient(135deg, ${backgroundColor || primaryColor}08 0%, transparent 100%)`,
    pointerEvents: 'none',
  }
}));

const TagGroupHeader = ({ tagGroup, extensions, expanded, onToggle, onChange, onDelete, t, isMobile }) => {
  const isAllTagGroup = tagGroup.toLowerCase() === ALL_TAG_GROUP.toLowerCase();
  const [extensionsInput, setExtensionsInput] = useState(extensions.join(', '));

  useEffect(() => {
    setExtensionsInput(extensions.join(', '));
  }, [extensions]);

  const handleExtensionsBlur = () => {
    const normalizedExtensions = normalizeExtensions(extensionsInput);
    setExtensionsInput(normalizedExtensions.join(', '));
    onChange({ extensions: normalizedExtensions });
  };

  return (
    <Paper
      elevation={0}
      sx={{
        position: 'relative',
        p: { xs: 1.5, sm: 2.25 },
        mb: 1.75,
        borderRadius: 3,
        border: `1px solid ${alpha(primaryColor, 0.16)}`,
        background: `linear-gradient(135deg, ${alpha(primaryColor, 0.1)} 0%, ${alpha('#ffffff', 0.92)} 58%, ${alpha(primaryColor, 0.035)} 100%)`,
        boxShadow: `0 8px 24px ${alpha(primaryColor, 0.08)}`,
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          background: `linear-gradient(90deg, ${alpha(primaryColor, 0.16)} 0, ${alpha(primaryColor, 0.16)} 5px, transparent 5px)`,
          pointerEvents: 'none',
        },
      }}
    >
      <Grid container spacing={isMobile ? 1.25 : 2} alignItems="center" sx={{ position: 'relative' }}>
        <Grid item xs={12} sm={4}>
          <TextField
            label={t('settings.tag_group_name', 'Tag group')}
            value={tagGroup}
            onChange={(event) => onChange({ [TAG_GROUP_FIELD]: event.target.value })}
            disabled={isAllTagGroup}
            fullWidth
            size="small"
            helperText={isAllTagGroup ? t('settings.tag_group_all_helper', 'Applied to every file') : ''}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                <FolderOutlinedIcon sx={{ color: isAllTagGroup ? primaryColor : 'text.secondary', fontSize: 21 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2.25,
                backgroundColor: alpha('#ffffff', 0.78),
                '& fieldset': { borderColor: alpha(primaryColor, 0.18) },
                '&:hover fieldset': { borderColor: alpha(primaryColor, 0.45) },
                '&.Mui-focused fieldset': { borderColor: primaryColor, borderWidth: 1.5 },
              },
              '& .MuiFormHelperText-root': { ml: 0.5 },
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label={t('settings.tag_group_extensions', 'File extensions')}
            value={extensionsInput}
            onChange={(event) => setExtensionsInput(event.target.value)}
            onBlur={handleExtensionsBlur}
            disabled={isAllTagGroup}
            fullWidth
            size="small"
            placeholder=".eml, .msg"
            helperText={isAllTagGroup ? t('settings.tag_group_all_extensions', 'No extension filter') : t('settings.tag_group_extensions_helper', 'Comma-separated, for example .eml, .msg')}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <ExtensionOutlinedIcon sx={{ color: isAllTagGroup ? 'text.disabled' : primaryColor, fontSize: 21 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2.25,
                backgroundColor: isAllTagGroup ? alpha('#ffffff', 0.48) : alpha('#ffffff', 0.86),
                '& fieldset': { borderColor: alpha(primaryColor, 0.18) },
                '&:hover fieldset': { borderColor: alpha(primaryColor, 0.45) },
                '&.Mui-focused fieldset': { borderColor: primaryColor, borderWidth: 1.5 },
              },
              '& .MuiFormHelperText-root': { ml: 0.5 },
            }}
          />
        </Grid>
        <Grid item xs={12} sm={2} sx={{ display: 'flex', justifyContent: { xs: 'flex-end', sm: 'center' } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            {!isAllTagGroup && (
              <Tooltip title={t('common.delete', 'Delete')}>
                <IconButton
                  color="error"
                  onClick={onDelete}
                  size="small"
                  sx={{
                    border: `1px solid ${alpha('#f44336', 0.18)}`,
                    backgroundColor: alpha('#ffffff', 0.56),
                    '&:hover': { backgroundColor: alpha('#f44336', 0.08) },
                  }}
                >
                  <RemoveIcon />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title={expanded ? t('settings.collapse_tag_group', 'Collapse group') : t('settings.expand_tag_group', 'Expand group')}>
              <IconButton
                onClick={onToggle}
                size="small"
                aria-expanded={expanded}
                sx={{
                  color: primaryColor,
                  border: `1px solid ${alpha(primaryColor, 0.2)}`,
                  backgroundColor: alpha('#ffffff', 0.62),
                  transition: 'transform 0.2s ease, background-color 0.2s ease',
                  '&:hover': { backgroundColor: alpha(primaryColor, 0.1) },
                }}
              >
                {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Tooltip>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

const SettingIntelligentTag = ({ onTagsChange, onTagsSaved }) => {
  const { caseid } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();
  const [editableTags, setEditableTags] = useState([]);
  const [originalTags, setOriginalTags] = useState([]);
  const [isChanged, setIsChanged] = useState(false);
  const [hasEverChanged, setHasEverChanged] = useState(false);  // Track if changes were ever made
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [errors, setErrors] = useState({});
  const [expandedFields, setExpandedFields] = useState({});
  const [expandedTagGroups, setExpandedTagGroups] = useState({});
  // Animation states
  const [isLoaded, setIsLoaded] = useState(false);
  const [animatingDelete, setAnimatingDelete] = useState({});
  const [savingIndex, setSavingIndex] = useState(null);
  const { tags, setTags } = useDashboard();
  const skipNextTagsSyncRef = useRef(false);
  const tagIdRef = useRef(0);

  const nextTagId = () => {
    tagIdRef.current += 1;
    return `tag-draft-${tagIdRef.current}`;
  };

  useEffect(() => {
    if (tags !== undefined && tags !== null) {
      if (skipNextTagsSyncRef.current) {
        skipNextTagsSyncRef.current = false;
        return;
      }

      try {
        const parsedTags = parseTagDefinitions(tags).map((tag) => ({
          ...tag,
          __tagId: tag.__tagId || `tag-draft-${++tagIdRef.current}`,
        }));
        setEditableTags(parsedTags);
        // Store original tags for later comparison
        setOriginalTags(JSON.parse(JSON.stringify(parsedTags)));
        setIsChanged(false);
      } catch (error) {
        setSnackbar({
          open: true,
          message: t('settings.tags_parse_error'),
          severity: 'error'
        });
        console.error('Error parsing tags:', error);
      }
    }
  }, [tags, t]);

  // Animation trigger
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoaded(true);
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  // Notify parent component when tags change
  useEffect(() => {
    // Determine if tags have structurally changed
    const checkTagsChanged = () => {
      return !areTagDefinitionsEqual(editableTags, originalTags);
    };

    const tagsChanged = checkTagsChanged();
    setIsChanged(tagsChanged);
    if (tagsChanged) {
      setHasEverChanged(true);
    }
    
    if (onTagsChange) {
      onTagsChange(hasEverChanged || tagsChanged);
    }
  }, [editableTags, originalTags, onTagsChange, hasEverChanged]);

  const tagGroups = editableTags.reduce((groups, tag, index) => {
    const tagGroup = tag[TAG_GROUP_FIELD] || ALL_TAG_GROUP;
    if (!groups[tagGroup]) {
      groups[tagGroup] = { tagGroup, extensions: tag.extensions || [], indexes: [] };
    }
    groups[tagGroup].indexes.push(index);
    return groups;
  }, {});
  const orderedTagIndexes = Object.values(tagGroups).flatMap(({ indexes }) => indexes);

  const updateTagGroup = (currentTagGroup, changes) => {
    setEditableTags((previous) => previous.map((tag) => {
      if ((tag[TAG_GROUP_FIELD] || ALL_TAG_GROUP) !== currentTagGroup) return tag;
      const nextTagGroup = changes[TAG_GROUP_FIELD] !== undefined
        ? String(changes[TAG_GROUP_FIELD]).trim()
        : currentTagGroup;
      return normalizeTagDefinition({
        ...tag,
        ...changes,
        [TAG_GROUP_FIELD]: nextTagGroup || currentTagGroup,
      });
    }));
  };

  const handleDeleteTagGroup = (tagGroup) => {
    setEditableTags((previous) => previous.filter((tag) => (tag[TAG_GROUP_FIELD] || ALL_TAG_GROUP) !== tagGroup));
  };

  const toggleTagGroup = (tagGroup) => {
    setExpandedTagGroups((previous) => ({
      ...previous,
      [tagGroup]: previous[tagGroup] === false,
    }));
  };

  const validateTag = (tag, index) => {
    const newErrors = {};
    let isValid = true;

    if (!tag.variable.trim()) {
      newErrors[`variable-${index}`] = t('settings.variable_required');
      isValid = false;
    } else {
      const originalTag = originalTags.find(
        (candidate) => candidate.__tagId === tag.__tagId,
      );
      const originalVariable = (originalTag?.variable || '').trim();
      const editedVariable = tag.variable.trim();
      if (
        editedVariable !== originalVariable
        && hasUserTagVariableReservedChars(editedVariable)
      ) {
        newErrors[`variable-${index}`] = t('settings.variable_no_space_underscore');
        isValid = false;
      }
    }

    if (!tag.description.trim()) {
      newErrors[`description-${index}`] = t('settings.description_required');
      isValid = false;
    }

    const tagGroup = String(tag[TAG_GROUP_FIELD] || ALL_TAG_GROUP).trim();
    const extensions = normalizeExtensions(tag.extensions);
    if (!tagGroup) {
      newErrors[`tagGroup-${index}`] = t('settings.tag_group_required', 'Tag group is required');
      isValid = false;
    } else if (
      tagGroup.toLowerCase() !== ALL_TAG_GROUP.toLowerCase()
      && (extensions.length === 0 || extensions.includes('.ext'))
    ) {
      newErrors[`tagGroup-${index}`] = t('settings.tag_group_extensions_required', 'Add at least one real file extension');
      isValid = false;
    }
    
    setErrors(prev => {
      const updatedErrors = { ...prev };
      delete updatedErrors[`variable-${index}`];
      delete updatedErrors[`description-${index}`];
      delete updatedErrors[`tagGroup-${index}`];
      return { ...updatedErrors, ...newErrors };
    });

    return isValid;
  };

  const hasTagChanged = (index) => {
    const edited = editableTags[index];
    const original = originalTags.find(
      (candidate) => candidate.__tagId === edited?.__tagId,
    );

    if (!edited) return false;
    if (!original) return true;

    return !areTagDefinitionsEqual([edited], [original]);
  };

  const buildSingleTagSavePayload = (index) => {
    const selectedTag = normalizeTagDefinition(editableTags[index]);
    const savedTags = originalTags.map(normalizeTagDefinition);
    const selectedTagIndex = savedTags.findIndex(
      (tag) => tag.__tagId === selectedTag.__tagId,
    );

    if (selectedTagIndex >= 0) {
      savedTags[selectedTagIndex] = selectedTag;
      return savedTags;
    }

    return [...savedTags, selectedTag];
  };

  const buildTagGroupSavePayload = (index) => {
    const selectedTag = normalizeTagDefinition(editableTags[index]);
    const originalSelectedTag = originalTags.find(
      (tag) => tag.__tagId === selectedTag.__tagId,
    );
    const currentTagGroup = selectedTag[TAG_GROUP_FIELD];
    const originalTagGroup = originalSelectedTag
      ? normalizeTagDefinition(originalSelectedTag)[TAG_GROUP_FIELD]
      : null;
    const currentTagGroupTags = editableTags
      .filter((tag) => normalizeTagDefinition(tag)[TAG_GROUP_FIELD] === currentTagGroup)
      .map(normalizeTagDefinition);
    const currentTagIds = new Set(currentTagGroupTags.map((tag) => tag.__tagId));
    const originalTagGroupIds = new Set(
      originalTagGroup === null
        ? []
        : originalTags
          .filter((tag) => normalizeTagDefinition(tag)[TAG_GROUP_FIELD] === originalTagGroup)
          .map((tag) => tag.__tagId),
    );
    const savedTags = originalTags
      .map(normalizeTagDefinition)
      .filter((tag) => (
        !originalTagGroupIds.has(tag.__tagId) || currentTagIds.has(tag.__tagId)
      ));

    currentTagGroupTags.forEach((tag) => {
      const savedTagIndex = savedTags.findIndex(
        (savedTag) => savedTag.__tagId === tag.__tagId,
      );
      if (savedTagIndex >= 0) {
        savedTags[savedTagIndex] = tag;
      } else {
        savedTags.push(tag);
      }
    });

    return savedTags;
  };

  const hasTagGroupChanged = (index) => {
    const edited = editableTags[index];
    const original = originalTags.find(
      (tag) => tag.__tagId === edited?.__tagId,
    );
    if (!original) return true;

    const currentTagGroup = normalizeTagDefinition(edited)[TAG_GROUP_FIELD];
    const originalTagGroup = normalizeTagDefinition(original)[TAG_GROUP_FIELD];
    const currentTagIds = new Set(
      editableTags
        .filter((tag) => normalizeTagDefinition(tag)[TAG_GROUP_FIELD] === currentTagGroup)
        .map((tag) => tag.__tagId),
    );
    const originalTagIds = new Set(
      originalTags
        .filter((tag) => normalizeTagDefinition(tag)[TAG_GROUP_FIELD] === originalTagGroup)
        .map((tag) => tag.__tagId),
    );
    const sameTagGroupMembers = currentTagIds.size === originalTagIds.size
      && [...currentTagIds].every((tagId) => originalTagIds.has(tagId));

    return !areTagGroupMetadataEqual(edited, original) || !sameTagGroupMembers;
  };

  const buildTagSavePayload = () => {
    return editableTags.map(normalizeTagDefinition);
  };

  const persistTags = async (tagsToSave, successMessageKey, fallbackMessage, saveIndex = null, displayTags = tagsToSave) => {
    setSavingIndex(saveIndex);

    try {
      const apiTags = tagsToSave.map(toApiTagDefinition);
      await apiRequest(`/api/v1/projects/${encodeURIComponent(caseid)}`, {
        method: 'PATCH',
        body: JSON.stringify({ customFields: apiTags }),
      });
      skipNextTagsSyncRef.current = true;
      setTags(JSON.stringify(apiTags));
      setEditableTags(displayTags);
      setOriginalTags(JSON.parse(JSON.stringify(tagsToSave)));
      if (onTagsSaved) {
        onTagsSaved();
      }

      setSnackbar({
        open: true,
        message: t(successMessageKey) || fallbackMessage,
        severity: 'success'
      });
    } catch (error) {
      console.error('Failed to save intelligent tags:', error);
      setSnackbar({
        open: true,
        message: error?.message || t('settings.connection_error'),
        severity: 'error'
      });
    } finally {
      setSavingIndex(null);
    }
  };

  const requestSaveTags = (tagsToSave, saveIndex = null, displayTags = tagsToSave) => {
    return persistTags(tagsToSave, 'settings.tags_saved', 'Tags saved successfully', saveIndex, displayTags);
  };

  const handleTagChange = (index, key, value) => {
    const updatedTags = [...editableTags];
    
    // Trim spaces from variable names
    if (key === 'variable') {
      updatedTags[index][key] = value.trim();
    } else {
      updatedTags[index][key] = value;
    }
    
    setEditableTags(updatedTags);
    
    // Clear error for this field if it exists
    if (errors[`${key}-${index}`]) {
      const updatedErrors = {...errors};
      delete updatedErrors[`${key}-${index}`];
      setErrors(updatedErrors);
    }
  };

  const handleAddTag = (tagGroup = ALL_TAG_GROUP) => {
    if (savingIndex !== null) {
      return;
    }

    const requestedTagGroup = String(tagGroup || ALL_TAG_GROUP).trim();
    const existingTagGroupKey = Object.keys(tagGroups).find(
      (existingTagGroup) => existingTagGroup.toLowerCase() === requestedTagGroup.toLowerCase(),
    );
    const resolvedTagGroup = existingTagGroupKey || requestedTagGroup || ALL_TAG_GROUP;
    setExpandedTagGroups((previous) => ({
      ...previous,
      [resolvedTagGroup]: true,
    }));
    setEditableTags((previous) => {
      const normalizedTagGroup = resolvedTagGroup.toLowerCase();
      let insertAt = previous.length;
      for (let index = previous.length - 1; index >= 0; index -= 1) {
        const currentTagGroup = String(previous[index]?.[TAG_GROUP_FIELD] || ALL_TAG_GROUP).trim().toLowerCase();
        if (currentTagGroup === normalizedTagGroup) {
          insertAt = index + 1;
          break;
        }
      }

      const updated = [...previous];
      updated.splice(insertAt, 0, normalizeTagDefinition({
        variable: '',
        description: '',
        [TAG_GROUP_FIELD]: resolvedTagGroup,
        extensions: tagGroups[resolvedTagGroup]?.extensions || [],
        __tagId: nextTagId(),
      }));
      return updated;
    });
  };

  const handleAddGroup = () => {
    if (savingIndex !== null) return;

    const existingTagGroups = Object.keys(tagGroups);
    const hasExistingTagGroup = (candidate) => existingTagGroups.some(
      (tagGroup) => tagGroup.toLowerCase() === candidate.toLowerCase(),
    );
    let tagGroupName = `New TagGroup ${existingTagGroups.length + 1}`;
    let suffix = 2;
    while (hasExistingTagGroup(tagGroupName)) {
      tagGroupName = `New TagGroup ${existingTagGroups.length + suffix}`;
      suffix += 1;
    }
    setEditableTags([
      ...editableTags,
      normalizeTagDefinition({
        variable: '',
        description: '',
        [TAG_GROUP_FIELD]: tagGroupName,
        extensions: ['.ext'],
        __tagId: nextTagId(),
      }),
    ]);
  };

  const handleDeleteTag = (index) => {
    setAnimatingDelete(prev => ({ ...prev, [index]: true }));
    
    setTimeout(() => {
      const updatedTags = editableTags.filter((_, i) => i !== index);
      setEditableTags(updatedTags);
      setAnimatingDelete(prev => {
        const newState = { ...prev };
        delete newState[index];
        return newState;
      });
    }, 200);
  };

  const handleSaveTag = (index) => {
    const tag = editableTags[index];

    if (!tag || !validateTag(tag, index)) {
      return;
    }

    const savePayload = hasTagGroupChanged(index)
      ? buildTagGroupSavePayload(index)
      : buildSingleTagSavePayload(index);
    requestSaveTags(savePayload, index, editableTags);
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({...snackbar, open: false});
  };

  const toggleDescriptionExpand = (index) => {
    setExpandedFields(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const handleClickAway = (index) => {
    setExpandedFields(prev => ({
      ...prev,
      [index]: false
    }));
  };

  return (
    <Fade in={isLoaded} timeout={600}>
      <Box>
        <Typography variant="h5" gutterBottom>
          {t('settings.intelligent_tags')}
          <Tooltip title={t("settings.manage_intelligent_tags")}>
            <IconButton sx={{ ml: 1, color: primaryColor, opacity: 0.7 }}>
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Typography>
        <Box sx={{ width: '100%', textAlign: 'center', mt: 1, mb: 2 }}>
          <Typography
            variant="body2"
            color="text.secondary"
            data-testid="intelligent-tags-description"
            sx={{ mx: 'auto', maxWidth: { xs: '100%', sm: 600 } }}
          >
            {t("settings.intelligent_tags_description", "Create smart tags to help AI automatically extract and organize information from your documents.")}
          </Typography>
        </Box>

      {orderedTagIndexes.map((index) => {
        const tag = editableTags[index];
        const tagGroup = tag[TAG_GROUP_FIELD] || ALL_TAG_GROUP;
        const tagGroupDetails = tagGroups[tagGroup] || { extensions: tag.extensions || [], indexes: [] };
        const isFirstTagInTagGroup = tagGroupDetails.indexes[0] === index;
        const isLastTagInTagGroup = tagGroupDetails.indexes[tagGroupDetails.indexes.length - 1] === index;
        const tagGroupNames = Object.keys(tagGroups);
        const isLastTagGroup = tagGroupNames[tagGroupNames.length - 1] === tagGroup;
        const isTagGroupExpanded = expandedTagGroups[tagGroup] !== false;
        return (
                <React.Fragment key={tag.__tagId || `tag-${index}`}>
            {isFirstTagInTagGroup && (
              <TagGroupHeader
                tagGroup={tagGroup}
                extensions={tagGroupDetails.extensions}
                expanded={isTagGroupExpanded}
                onToggle={() => toggleTagGroup(tagGroup)}
                onChange={(changes) => updateTagGroup(tagGroup, changes)}
                onDelete={() => handleDeleteTagGroup(tagGroup)}
                t={t}
                isMobile={isMobile}
              />
            )}
            <Collapse in={isTagGroupExpanded} timeout="auto" unmountOnExit={false}>
              <Grow in={!animatingDelete[index]} timeout={300 + index * 100}>
          <StyledTagPaper 
            elevation={0} 
            backgroundColor={generatePastelColor(tag.variable || index.toString())}
            sx={{ 
              px: { xs: 1.5, sm: 2.5 },
              py: { xs: 1.5, sm: 2.5 },
              mx: 0
            }}
          >
            <Grid container spacing={isMobile ? 1.5 : 3} alignItems={isMobile ? "center" : "flex-start"}>
              <Grid item xs={10} sm={4} sx={{ order: { xs: 1, sm: 1 } }}>
                <TextField
                  label={t('settings.variable')}
                  value={tag.variable}
                  onChange={(e) => handleTagChange(index, 'variable', e.target.value)}
                  fullWidth
                  error={!!errors[`variable-${index}`]}
                  helperText={errors[`variable-${index}`] || ''}
                  required
                  variant="outlined"
                  size={isMobile ? "small" : "medium"}
                  InputProps={{
                    startAdornment: (
                      <Box 
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: '8px',
                          backgroundColor: `${generateSaturatedColor(tag.variable || index.toString())}15`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: generateSaturatedColor(tag.variable || index.toString()),
                          fontSize: '11px',
                          fontWeight: '800',
                          mr: 1,
                          flexShrink: 0,
                          border: `1px solid ${generateSaturatedColor(tag.variable || index.toString())}30`,
                          textShadow: '0 1px 0 rgba(255,255,255,0.5)'
                        }}
                      >
                        {(tag.variable || 'T').charAt(0).toUpperCase()}
                      </Box>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: 'rgba(255, 255, 255, 0.6)',
                      '& fieldset': { borderColor: 'rgba(0,0,0,0.08)' },
                      '&:hover fieldset': { borderColor: primaryColor },
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} sx={{ order: { xs: 3, sm: 2 } }}>
                <ClickAwayListener onClickAway={() => handleClickAway(index)}>
                  <Box sx={{ position: 'relative' }}>
                    <TextField
                      label={t('settings.description')}
                      value={tag.description}
                      onChange={(e) => handleTagChange(index, 'description', e.target.value)}
                      fullWidth
                      multiline
                      rows={expandedFields[index] ? 4 : 1}
                      error={!!errors[`description-${index}`]}
                      helperText={errors[`description-${index}`] || (expandedFields[index] ? t('settings.tag_description_tip') : '')}
                      required
                      onClick={() => !expandedFields[index] && toggleDescriptionExpand(index)}
                      variant="outlined"
                      size={isMobile ? "small" : "medium"}
                      InputProps={{
                        startAdornment: !expandedFields[index] && (
                          <DescriptionIcon color="action" sx={{ mr: 1, opacity: 0.7, fontSize: 20, alignSelf: 'center' }} />
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          transition: 'all 0.2s ease-in-out',
                          borderRadius: 2,
                          pr: 4, // Space for expand icon
                          backgroundColor: 'rgba(255, 255, 255, 0.6)',
                          '& fieldset': { borderColor: 'rgba(0,0,0,0.08)' },
                          '&:hover fieldset': { borderColor: primaryColor },
                        },
                        // Hide scrollbar when collapsed, show when expanded
                        '& .MuiInputBase-root': {
                          overflow: expandedFields[index] ? 'auto' : 'hidden',
                        },
                        '& .MuiInputBase-input': {
                          overflow: expandedFields[index] ? 'auto' : 'hidden',
                          textOverflow: expandedFields[index] ? 'clip' : 'ellipsis',
                          whiteSpace: expandedFields[index] ? 'pre-wrap' : 'nowrap',
                        }
                      }}
                    />
                    <IconButton 
                      size="small"
                      onClick={() => toggleDescriptionExpand(index)}
                      sx={{ 
                        position: 'absolute', 
                        right: 8, 
                        top: 8,
                        color: primaryColor,
                        backgroundColor: alpha(primaryColor, 0.05),
                        '&:hover': { 
                          backgroundColor: alpha(primaryColor, 0.15) 
                        },
                        zIndex: 1
                      }}
                    >
                      {expandedFields[index] ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                    </IconButton>
                  </Box>
                </ClickAwayListener>
              </Grid>
              <Grid item xs={2} sm={2} sx={{ display: 'flex', alignItems: isMobile ? 'center' : 'flex-start', justifyContent: 'center', gap: 1, flexDirection: { xs: 'column', sm: 'row' }, pt: isMobile ? 1.5 : 1, order: { xs: 2, sm: 3 } }}>
                <Tooltip title={t('common.save') || t('settings.save_tags')}>
                  <span>
                    <IconButton
                      onClick={() => handleSaveTag(index)}
                      disabled={savingIndex !== null || !hasTagChanged(index)}
                      size="small"
                      sx={{
                        color: primaryColor,
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        backgroundColor: 'rgba(255, 255, 255, 0.5)',
                        border: '1px solid',
                        borderColor: 'rgba(0,0,0,0.05)',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          backgroundColor: alpha(primaryColor, 0.08),
                          transform: 'scale(1.1)',
                          borderColor: alpha(primaryColor, 0.2),
                        },
                        '&.Mui-disabled': {
                          color: alpha(primaryColor, 0.3),
                          backgroundColor: 'rgba(255, 255, 255, 0.35)',
                        }
                      }}
                    >
                      {savingIndex === index ? <CircularProgress size={18} color="inherit" /> : <SaveIcon fontSize="small" />}
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title={t('common.delete') || "Delete"}>
                  <IconButton
                    onClick={() => handleDeleteTag(index)}
                    disabled={savingIndex !== null}
                    size="small"
                    sx={{
                      color: 'error.main',
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      backgroundColor: 'rgba(255, 255, 255, 0.5)',
                      border: '1px solid',
                      borderColor: 'rgba(0,0,0,0.05)',
                      transition: 'all 0.2s ease',
                      '&:hover': { 
                        backgroundColor: alpha(theme.palette.error.main, 0.08),
                        transform: 'scale(1.1)',
                        borderColor: alpha(theme.palette.error.main, 0.2),
                      }
                    }}
                  >
                    <RemoveIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Grid>
            </Grid>
            </StyledTagPaper>
              </Grow>
              {isLastTagInTagGroup && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 0.5, mb: 1.5 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => handleAddTag(tagGroup)}
                    disabled={savingIndex !== null}
                    sx={{
                      px: 2.25,
                      py: 0.8,
                      borderRadius: 2.5,
                      color: primaryColor,
                      borderColor: alpha(primaryColor, 0.28),
                      backgroundColor: alpha('#ffffff', 0.62),
                      '&:hover': {
                        borderColor: primaryColor,
                        backgroundColor: alpha(primaryColor, 0.08),
                      },
                    }}
                  >
                    {t('settings.add_tag_to_group', 'Add tag')}
                  </Button>
                </Box>
              )}
            </Collapse>
            {isLastTagInTagGroup && !isLastTagGroup && (
              <Box
                aria-hidden="true"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  my: { xs: 2.5, sm: 3.5 },
                  px: { xs: 1, sm: 2 },
                }}
              >
                <Box sx={{ flex: 1, height: 1, background: `linear-gradient(90deg, transparent, ${alpha(primaryColor, 0.24)})` }} />
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: primaryColor, boxShadow: `0 0 0 5px ${alpha(primaryColor, 0.09)}` }} />
                <Box sx={{ flex: 1, height: 1, background: `linear-gradient(90deg, ${alpha(primaryColor, 0.24)}, transparent)` }} />
              </Box>
            )}
          </React.Fragment>
        );
      })}

      <Box sx={{ mt: 3, mb: 4, width: '100%', display: 'flex', justifyContent: 'center' }}>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => handleAddTag(ALL_TAG_GROUP)}
          disabled={savingIndex !== null}
          sx={{ borderRadius: 2, minHeight: 56 }}
        >
          {t('settings.add_tag_to_group', 'Add tag')}
        </Button>
        <Button
          variant="outlined"
          onClick={handleAddGroup}
          disabled={savingIndex !== null}
          sx={{ ml: 2, borderRadius: 2, minHeight: 56 }}
        >
          {t('settings.add_tag_group', 'Add group')}
        </Button>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
        <Button
          variant="contained"
          startIcon={savingIndex === -1 ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
          onClick={() => {
            const invalidIndex = editableTags.findIndex((tag) => !validateTag(tag, editableTags.indexOf(tag)));
            if (invalidIndex === -1) {
              requestSaveTags(buildTagSavePayload(), -1, editableTags);
            }
          }}
          disabled={savingIndex !== null || !isChanged}
        >
          {t('settings.save_tag_groups', 'Save tag groups')}
        </Button>
      </Box>

      <Typography variant="caption" display="block" color="textSecondary" sx={{ mb: 4, textAlign: 'center', maxWidth: 500, mx: 'auto', opacity: 0.8, lineHeight: 1.6 }}>
        {t('settings.tags_save_note')}
      </Typography>

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
    </Fade>
  );
};

export default SettingIntelligentTag;
